/**
 * @lines98/shared/engine/lines98 — Lines 98 deterministic engine + bot simulator.
 *
 * Per ADR-0001 §A3 (engine lives in shared/, single source of truth для anti-cheat).
 * Per anti-cheat.md §8 «один движок, без дубля»: client and server import the same code.
 *
 * SCOPE:
 *   - Pure deterministic game engine: same seed → same start + same ball stream.
 *   - applyMove / checkLines / replayScore / startState / spawnNext / genNext / scoreFor.
 *   - genBotMoves: greedy bot heuristic для leaderboard generation + balance simulation.
 *   - NO DOM access. Works in browser, Node, vitest, web workers.
 *
 * NOT IN SCOPE (deferred):
 *   - engine_version registry — ADR-0004, implemented в T-11.
 *   - Payload-size validation — happens at the server boundary, NOT here. Per
 *     ADR-0018 (owner-directive «БЕЗ КАПА») there is NO move-count cap anywhere;
 *     the server bounds the raw request body in bytes (`MAX_SUBMIT_PAYLOAD_BYTES`,
 *     Fastify per-route `bodyLimit`) — a RESOURCE limit, not a game rule. The
 *     engine replays a `moves[]` of ANY length deterministically and never knows
 *     about any limit. anti-cheat.md §5.3.
 *   - Server-side seed derivation — ADR-0002, implemented в T-3.
 *
 * SOURCE OF SEMANTICS:
 *   - This file is the TS port of the engine block в `docs/prototype/lines98-concept.html`
 *     (lines ~445–520), which is the canonical working version owner approved.
 *   - The pre-existing `src/lines98-engine.js` was genuinely truncated mid-`genBotMoves`
 *     and has been removed; the prototype HTML is the source of truth for parity.
 *   - RNG, iteration order, BFS direction order are preserved bit-for-bit. Replays
 *     against this engine match the prototype byte-for-byte (golden-tests AC-1).
 *
 * CURRENT ENGINE VERSION: **1.1.0** (CURRENT_ENGINE_VERSION, balance.ts). The
 * 1.0.0 → 1.1.0 bump (ADR-0004 addendum-001, 2026-06-07) fixes `spawnNext`
 * ghost-reroll (ghost-spawn-determinism.md §2.2 — a ghost can no longer displace
 * another ghost). This is the ONLY semantic delta from 1.0.0; the frozen 1.0.0
 * snapshot lives in `versions/v1.0.0/lines98.ts` (DO NOT EDIT). Any future engine
 * change = new version snapshot, never a silent edit (ADR-0004 §«Failure modes»).
 *
 * Cross-references:
 *   - ADR-0001 §A3 (shared engine location)
 *   - ADR-0004 + addendum-001 (engine versioning, this ghost-reroll bump)
 *   - ghost-spawn-determinism.md §2.2–§2.3, §6 (spawnNext reroll semantics)
 *   - anti-cheat.md §8 (single-engine invariant)
 *   - core-daily-board.md §4 (Game = seed + moves[] + mode)
 *   - replays-ghosts.md §4 (Move shape)
 */
import { N, COLORS, INITIAL_BALLS_COUNT, NEXT_BALLS_PER_TURN, MIN_LINE_LENGTH } from '../config/balance.js';
// =============================================================================
// Seeded RNG (LCG — mulberry-like). Deterministic by seed.
// =============================================================================
/**
 * Make a seeded random number generator (LCG, mulberry-like).
 *
 * Same seed → identical stream of `[0, 1)` floats forever. Used everywhere в the
 * engine for any randomness, so a single `seed: uint32` fully determines a game.
 *
 * @param seed — uint32 seed; 0 is coerced к 1 (LCG with state=0 is degenerate).
 */
export function makeRng(seed) {
    let s = (seed >>> 0) || 1;
    return () => {
        s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
        return s / 4294967296;
    };
}
// =============================================================================
// Board helpers
// =============================================================================
const inb = (r, c) => r >= 0 && r < N && c >= 0 && c < N;
function emptyCells(g) {
    const a = [];
    for (let r = 0; r < N; r++) {
        for (let c = 0; c < N; c++) {
            if (g[r][c] === 0)
                a.push([r, c]);
        }
    }
    return a;
}
function placeBalls(st, count) {
    const ri = (n) => Math.floor(st.rng() * n);
    for (let i = 0; i < count; i++) {
        const e = emptyCells(st.grid);
        if (!e.length)
            return;
        const [r, c] = e[ri(e.length)];
        st.grid[r][c] = (1 + ri(COLORS));
    }
}
/**
 * Generate the «next» 3 balls (their colors + target cells). Visible в `predict`
 * mode (3 ghosts on the board); visible in niche only in `classic`; hidden in `blind`.
 *
 * Implementation detail: the RNG sequence MUST stay identical to the prototype —
 * `emptyCells()` snapshot + `splice` per ball + color draw. Re-ordering this breaks
 * replay determinism (golden-tests AC-1).
 */
export function genNext(st) {
    const ri = (n) => Math.floor(st.rng() * n);
    st.next = [];
    const e = emptyCells(st.grid);
    for (let i = 0; i < NEXT_BALLS_PER_TURN && e.length; i++) {
        const [r, c] = e.splice(ri(e.length), 1)[0];
        st.next.push({ color: (1 + ri(COLORS)), r, c });
    }
}
// =============================================================================
// Pathfinding + line detection
// =============================================================================
/** BFS: is there a path through empty cells from (sr,sc) к (tr,tc)? */
function hasPath(g, sr, sc, tr, tc) {
    const seen = [];
    for (let i = 0; i < N; i++)
        seen.push(new Array(N).fill(false));
    const q = [[sr, sc]];
    seen[sr][sc] = true;
    const dirs = [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
    ];
    while (q.length) {
        const [r, c] = q.shift();
        if (r === tr && c === tc)
            return true;
        for (let d = 0; d < 4; d++) {
            const nr = r + dirs[d][0];
            const nc = c + dirs[d][1];
            if (inb(nr, nc) && !seen[nr][nc] && g[nr][nc] === 0) {
                seen[nr][nc] = true;
                q.push([nr, nc]);
            }
        }
    }
    return false;
}
/**
 * Find the BFS path of empty cells from `from` to `to`.
 *
 * VISUAL-ONLY helper (board-feedback-animations.md §2.5). The returned path is the
 * sequence of cells the ball travels through, **including both endpoints**:
 * `[ {r:sr,c:sc}, …intermediate empties…, {r:tr,c:tc} ]`. Returns `null` when no
 * path exists (same connectivity model as `hasPath`).
 *
 * INVARIANTS (load-bearing — anti-cheat.md §3.6, ADR-0004):
 *   - **Same traversal order as `hasPath`** — identical `dirs` order + FIFO queue,
 *     fixed bit-for-bit (file header L23). BFS predecessors are recorded in that
 *     same order, so the backtracked path is DETERMINISTIC: same `grid + from + to`
 *     → same path (mirrors what a player saw / the single canonical reachability).
 *   - **Additive + visual-only.** NOT called by `applyMove` / `replayScore` /
 *     `spawnNext` / scoring; does NOT mutate the grid; never part of game-state
 *     logic → the score path is byte-for-byte unchanged. NOT a breaking change
 *     (ADR-0004 §«Что считается breaking»: the frozen set —
 *     applyMove/checkLines/scoreFor/spawnNext/genNext/startState/hasPath — is
 *     untouched, and this new function is not in it). NO engine version bump.
 *
 * The caller passes an empty `to` cell (a real move always lands on empty —
 * `applyMove` rejects occupied targets). If `from === to`, returns `[from]`.
 */
export function findPath(g, from, to) {
    const sr = from.r;
    const sc = from.c;
    const tr = to.r;
    const tc = to.c;
    // prev[r*N+c] = key of the cell we came from, -1 for the start, or undefined
    // when unvisited. Mirrors hasPath's `seen` array + adds backtracking.
    const prev = new Array(N * N).fill(undefined);
    const q = [[sr, sc]];
    prev[sr * N + sc] = -1;
    // SAME direction order as hasPath (file header L23) — keeps the path deterministic.
    const dirs = [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
    ];
    while (q.length) {
        const [r, c] = q.shift();
        if (r === tr && c === tc) {
            // Backtrack target → start via `prev`, then reverse → start..target.
            const path = [];
            let cur = r * N + c;
            while (cur !== -1) {
                path.push({ r: Math.floor(cur / N), c: cur % N });
                cur = prev[cur];
            }
            path.reverse();
            return path;
        }
        for (let d = 0; d < 4; d++) {
            const nr = r + dirs[d][0];
            const nc = c + dirs[d][1];
            if (inb(nr, nc) && prev[nr * N + nc] === undefined && g[nr][nc] === 0) {
                prev[nr * N + nc] = r * N + c;
                q.push([nr, nc]);
            }
        }
    }
    return null;
}
/**
 * Check lines (length >= MIN_LINE_LENGTH) of one color passing through (r,c).
 * Returns the list of cells to remove.
 */
export function checkLines(g, r, c) {
    const color = g[r][c];
    if (!color)
        return [];
    const dirs = [
        [0, 1],
        [1, 0],
        [1, 1],
        [1, -1],
    ];
    const set = {};
    for (let d = 0; d < dirs.length; d++) {
        const dr = dirs[d][0];
        const dc = dirs[d][1];
        const line = [[r, c]];
        for (let s = -1; s <= 1; s += 2) {
            let nr = r + dr * s;
            let nc = c + dc * s;
            while (inb(nr, nc) && g[nr][nc] === color) {
                line.push([nr, nc]);
                nr += dr * s;
                nc += dc * s;
            }
        }
        if (line.length >= MIN_LINE_LENGTH) {
            for (let i = 0; i < line.length; i++) {
                set[line[i][0] * N + line[i][1]] = 1;
            }
        }
    }
    return Object.keys(set).map((k) => {
        const ki = +k;
        return [Math.floor(ki / N), ki % N];
    });
}
/**
 * Score formula for one cleared line of `n` balls.
 *
 * Classic Lines 98: 5→5, 6→11, 7→18, 8→26, 9→35. Rewards longer lines.
 * Owner-fixed concept-stage (PROJECT.md). NEVER change without updating
 * `docs/balance/` simulations (CLAUDE.md hard rule).
 */
export function scoreFor(n) {
    return (n * (n + 1)) / 2 - 10;
}
/**
 * One BFS from a ball — mask of reachable empty cells. Faster than hasPath на pair.
 *
 * Exposed для UI: клиент использует mask для hover-feedback (подсветка
 * недостижимых клеток розовым при выбранном шаре). Возвращает 9×9 boolean grid
 * где `true` = пустая клетка достижима от (sr, sc). Сам ball-cell (sr, sc) и
 * клетки с шарами остаются `false`.
 */
export function reachMask(g, sr, sc) {
    const seen = [];
    for (let i = 0; i < N; i++)
        seen.push(new Array(N).fill(false));
    const q = [];
    const dirs = [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
    ];
    for (let d = 0; d < 4; d++) {
        const nr = sr + dirs[d][0];
        const nc = sc + dirs[d][1];
        if (inb(nr, nc) && g[nr][nc] === 0) {
            seen[nr][nc] = true;
            q.push([nr, nc]);
        }
    }
    while (q.length) {
        const [r, c] = q.shift();
        for (let k = 0; k < 4; k++) {
            const ar = r + dirs[k][0];
            const ac = c + dirs[k][1];
            if (inb(ar, ac) && !seen[ar][ac] && g[ar][ac] === 0) {
                seen[ar][ac] = true;
                q.push([ar, ac]);
            }
        }
    }
    return seen;
}
/** Length of the longest single-color run through (r,c). Used by the bot. */
function maxRunAt(g, r, c, color) {
    let best = 1;
    const dirs = [
        [0, 1],
        [1, 0],
        [1, 1],
        [1, -1],
    ];
    for (let d = 0; d < dirs.length; d++) {
        const dr = dirs[d][0];
        const dc = dirs[d][1];
        let len = 1;
        for (let s = -1; s <= 1; s += 2) {
            let nr = r + dr * s;
            let nc = c + dc * s;
            while (inb(nr, nc) && g[nr][nc] === color) {
                len++;
                nr += dr * s;
                nc += dc * s;
            }
        }
        if (len > best)
            best = len;
    }
    return best;
}
// =============================================================================
// Public game operations
// =============================================================================
/**
 * Spawn the "next" balls into real positions and check lines.
 *
 * Called after every move that did NOT clear a line — the «3 balls drop in» moment.
 *
 * GHOST-REROLL (ghost-spawn-determinism.md §2.2–§2.3, engine v1.1.0 / ADR-0004
 * addendum-001, owner-classified the prior behaviour as a bug 2026-06-07):
 * a next-ball whose target cell is occupied (the player moved exactly onto a
 * ghost cell) re-rolls. The reroll set is
 *
 *     ДОПУСТИМЫЕ = emptyCells(grid)  MINUS  target cells of ghosts of THIS batch
 *                  that come AFTER the current one and have NOT been seated yet.
 *
 * Reserving the still-to-be-placed ghosts' target cells (even if those ghosts will
 * themselves later reroll) makes «ghost displaces ghost» constructively impossible:
 * a reroll can never land on a cell another ghost is still going to claim, so no
 * later ghost ever sees «occupied» because of a neighbour's reroll (I-1, I-2, §6.3).
 * Ghosts seated BEFORE the current one are already on the grid → not in
 * `emptyCells` → no extra exclusion needed (§2.2 rationale).
 *
 * DETERMINISM (I-3, anti-cheat.md): exactly ONE `st.rng()` draw per reroll, indexing
 * into the row-major filtered list (`emptyCells` order preserved by the filter), so
 * client / server / bot stay bit-for-bit identical. v1.0.0 (pre-fix) rerolled from
 * ALL empty cells — frozen in versions/v1.0.0/lines98.ts.
 *
 * EDGE (§6.1): if the allowed set is empty (every empty cell is reserved, or none
 * exist), the ghost is SKIPPED — no displacement, never a cascade. Matches the
 * pre-fix `if (!e.length) return` shape; `st.next` may materialize < 3 balls
 * (core-daily-board.md §7.5).
 *
 * Returns the cells cleared by lines during the spawn (board-feedback-animations.md
 * §2.3). The return is an ADDITIVE UI hint — grid mutation + score are unchanged
 * whether the caller reads it (bot/replay ignore it). `grown` is `true` when the
 * cleared cell was one of the just-spawned positions (`made`).
 */
export function spawnNext(st) {
    const made = [];
    // Indexed loop (not forEach) so each ghost can reserve the target cells of the
    // ghosts that come AFTER it in placement order (still-to-be-placed of this batch).
    for (let i = 0; i < st.next.length; i++) {
        const g = st.next[i];
        let r = g.r;
        let c = g.c;
        if (st.grid[r][c] !== 0) {
            // Reserve target cells of ghosts j > i (still-to-be-placed) — they may NOT be
            // chosen by this reroll (ghost-spawn-determinism.md §2.2). A Set keyed by
            // r*N+c keeps the membership test O(1) without touching iteration order.
            const reserved = new Set();
            for (let j = i + 1; j < st.next.length; j++) {
                const f = st.next[j];
                reserved.add(f.r * N + f.c);
            }
            // Allowed = empty cells (row-major, like emptyCells) minus reserved future
            // ghost cells. Filtering preserves the row-major order, so the RNG index maps
            // to the same stable list on client and server (I-3).
            const allowed = emptyCells(st.grid).filter(([er, ec]) => !reserved.has(er * N + ec));
            if (!allowed.length)
                continue; // §6.1: no room → skip, no displacement.
            const p = allowed[Math.floor(st.rng() * allowed.length)];
            r = p[0];
            c = p[1];
        }
        st.grid[r][c] = g.color;
        made.push([r, c]);
    }
    // Set of just-spawned positions (key = r*N+c) — drives the `grown` flag.
    const madeKeys = new Set(made.map((p) => p[0] * N + p[1]));
    // Dedup cleared cells by (r,c) — two spawned lines may intersect (same line color).
    const cleared = new Map();
    made.forEach((p) => {
        const cl = checkLines(st.grid, p[0], p[1]);
        if (cl.length) {
            cl.forEach((q) => {
                const key = q[0] * N + q[1];
                if (!cleared.has(key)) {
                    // Read colour BEFORE zeroing (= line colour).
                    const color = st.grid[q[0]][q[1]];
                    cleared.set(key, { r: q[0], c: q[1], color, grown: madeKeys.has(key) });
                }
                st.grid[q[0]][q[1]] = 0;
            });
            st.score += scoreFor(cl.length);
            st.lines += cl.length;
        }
    });
    genNext(st);
    return [...cleared.values()];
}
/**
 * Apply a move. Returns `{ ok: false }` if no path / target occupied.
 *
 * Single implementation used by:
 *   - live game (client UI),
 *   - bot simulator (genBotMoves),
 *   - server-side replay verification (replayScore — anti-cheat).
 *
 * The combo counter is preserved bit-for-bit с прототипа — it isn't used by
 * scoring (only by score popup UX), but downstream tests + later UI work expect it.
 */
export function applyMove(st, sr, sc, r, c) {
    if (st.grid[r][c] !== 0)
        return { ok: false, reason: 'occupied' };
    if (!hasPath(st.grid, sr, sc, r, c))
        return { ok: false, reason: 'nopath' };
    st.grid[r][c] = st.grid[sr][sc];
    st.grid[sr][sc] = 0;
    const cl = checkLines(st.grid, r, c);
    if (cl.length) {
        // Direct-move clear (board-feedback-animations.md §2.3, viaSpawn:false).
        // Snapshot colour BEFORE zeroing. `cl` includes dst (held the moved ball);
        // source is already empty from the move above → naturally excluded (I-2).
        const cleared = cl.map(([cr, cc]) => ({
            r: cr,
            c: cc,
            color: st.grid[cr][cc],
            grown: false,
        }));
        cl.forEach((q) => {
            st.grid[q[0]][q[1]] = 0;
        });
        st.score += scoreFor(cl.length);
        st.lines += cl.length;
        st.combo = Math.min(st.combo + 1, 9);
        return { ok: true, cleared: cl.length, events: { cleared, viaSpawn: false } };
    }
    st.combo = 1;
    const spawnCleared = spawnNext(st);
    if (spawnCleared.length) {
        return { ok: true, cleared: 0, events: { cleared: spawnCleared, viaSpawn: true } };
    }
    return { ok: true, cleared: 0 };
}
/**
 * Start state. One seed → identical start across all players.
 *
 * Order of ops mirrors the prototype exactly:
 *   1. Build empty 9×9 grid.
 *   2. Build game state with RNG seeded by `seed`.
 *   3. placeBalls(5) — initial layout via RNG.
 *   4. genNext() — generate the first «next 3».
 */
export function startState(seed) {
    const grid = [];
    for (let i = 0; i < N; i++)
        grid.push(new Array(N).fill(0));
    const st = {
        grid,
        next: [],
        score: 0,
        combo: 1,
        lines: 0,
        rng: makeRng(seed),
        seed,
    };
    placeBalls(st, INITIAL_BALLS_COUNT);
    genNext(st);
    return st;
}
/**
 * Replay verification (server-side anti-cheat).
 *
 * Re-runs the full game from `seed + moves` and returns either:
 *   - `{ valid: true, score, lines, moves }` — replays the original score, or
 *   - `{ valid: false, atMove: i }` — the i-th move failed `applyMove`.
 *
 * Per anti-cheat.md §3.1: any single invalid move rejects the whole game (no partial credit).
 * Per AC-4 (anti-cheat.md): 100 re-runs must give identical score (deterministic).
 */
export function replayScore(seed, moves) {
    const st = startState(seed);
    for (let i = 0; i < moves.length; i++) {
        const m = moves[i];
        const res = applyMove(st, m.sr, m.sc, m.r, m.c);
        if (!res.ok)
            return { valid: false, atMove: i };
    }
    return { valid: true, score: st.score, lines: st.lines, moves: moves.length };
}
// =============================================================================
// Bot simulator (greedy «build a line» heuristic)
// =============================================================================
function shuffle(arr, rng) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const t = a[i];
        a[i] = a[j];
        a[j] = t;
    }
    return a;
}
/**
 * Bot simulator: greedy heuristic «build a line».
 *
 * Picks the move that maximizes the longest same-color run at the destination.
 * A ready line (>=MIN_LINE_LENGTH) is always top-priority. `decisionSeed` varies
 * games between «players» on the same daily board. `boardSeed` fixes the layout
 * (shared across players on doska dnya).
 *
 * Iteration order MATCHES the prototype (lines98-concept.html §«BOT»):
 *   - shuffle balls by decision RNG → for each ball, BFS reachable mask →
 *     iterate (r, c) over full N×N grid (not just empties) → score val.
 *
 * @param boardSeed — uint32 board seed (initial layout + ball stream).
 * @param decisionSeed — uint32 decision-RNG seed (varies the bot per «player»).
 * @param maxTurns — safety cap (default 200; T-1 worst-case observed ≤ this).
 * @returns `{ moves, score, lines }`.
 */
export function genBotMoves(boardSeed, decisionSeed, maxTurns = 200) {
    const st = startState(boardSeed);
    const dr = makeRng(decisionSeed);
    const moves = [];
    for (let t = 0; t < maxTurns; t++) {
        const balls = [];
        for (let r = 0; r < N; r++) {
            for (let c = 0; c < N; c++) {
                if (st.grid[r][c])
                    balls.push([r, c]);
            }
        }
        if (!emptyCells(st.grid).length)
            break;
        let best = null;
        let bestVal = -1;
        const bShuf = shuffle(balls, dr);
        for (let bi = 0; bi < bShuf.length; bi++) {
            const sr = bShuf[bi][0];
            const sc = bShuf[bi][1];
            const col = st.grid[sr][sc];
            const mask = reachMask(st.grid, sr, sc);
            for (let r = 0; r < N; r++) {
                for (let c = 0; c < N; c++) {
                    if (!mask[r][c])
                        continue;
                    st.grid[r][c] = col;
                    st.grid[sr][sc] = 0;
                    const run = maxRunAt(st.grid, r, c, col);
                    st.grid[sr][sc] = col;
                    st.grid[r][c] = 0;
                    const val = run >= MIN_LINE_LENGTH ? 1000 + run : run + dr() * 0.4;
                    if (val > bestVal) {
                        bestVal = val;
                        best = [sr, sc, r, c];
                    }
                }
            }
        }
        if (!best)
            break;
        moves.push({ sr: best[0], sc: best[1], r: best[2], c: best[3] });
        applyMove(st, best[0], best[1], best[2], best[3]);
    }
    return { moves, score: st.score, lines: st.lines };
}
//# sourceMappingURL=lines98.js.map